/**
 * Welcome modal for first-time visitors
 * Presents choice between New Trader (tour) and Professional (skip)
 * Note: Hidden on mobile devices - we want users to download the app instead
 */

import { useState, useEffect } from "react";
import { useOnboarding } from "./use-onboarding";
import { cn } from "@/lib/utils";
import { Sparkles, Zap, TrendingUp, Shield } from "lucide-react";

export const WelcomeModal = () => {
  const { showWelcomeModal, selectNewTrader, selectProfessional } = useOnboarding();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if user is on mobile device
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /iphone|ipad|ipod|android|webos|blackberry|windows phone/i.test(userAgent);
      const isSmallScreen = window.innerWidth < 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Don't show on mobile - we want them to download the app instead
  if (isMobile) return null;

  if (!showWelcomeModal) return null;

  return (
    <div className="fixed inset-0 z-[10001] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />

      {/* Modal */}
      <div
        className={cn(
          "relative w-full max-w-lg",
          "bg-card border border-border rounded-2xl shadow-2xl",
          "animate-in fade-in-0 zoom-in-95 duration-300"
        )}
      >
        {/* Decorative gradient bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-emerald-500 via-emerald-400 to-teal-500 rounded-t-2xl" />

        {/* Header */}
        <div className="px-8 pt-8 pb-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 mb-4">
            <TrendingUp className="size-8 text-emerald-500" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">
            Welcome to Nordex
          </h2>
          <p className="text-muted-foreground text-sm">
            Perpetual trading, reimagined. Let's get you set up.
          </p>
        </div>

        {/* Options */}
        <div className="px-6 pb-6">
          <p className="text-center text-sm text-muted-foreground mb-4">
            How would you like to start?
          </p>

          <div className="grid gap-4">
            {/* New Trader Option */}
            <button
              onClick={selectNewTrader}
              className={cn(
                "group relative w-full p-5 rounded-xl text-left transition-all duration-200",
                "bg-gradient-to-br from-emerald-500/10 to-emerald-600/5",
                "border-2 border-emerald-500/30 hover:border-emerald-500/60",
                "hover:shadow-lg hover:shadow-emerald-500/10",
                "focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:ring-offset-2 focus:ring-offset-background"
              )}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Sparkles className="size-6 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground mb-1 flex items-center gap-2">
                    I'm New to Trading
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400">
                      Recommended
                    </span>
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Take a quick interactive tour of the platform. Learn the basics and customize your layout.
                  </p>
                </div>
              </div>
              {/* Arrow indicator */}
              <div className="absolute right-4 top-1/2 -translate-y-1/2 text-emerald-500 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
                →
              </div>
            </button>

            {/* Professional Option */}
            <button
              onClick={selectProfessional}
              className={cn(
                "group relative w-full p-5 rounded-xl text-left transition-all duration-200",
                "bg-muted/30 hover:bg-muted/50",
                "border border-border hover:border-muted-foreground/30",
                "focus:outline-none focus:ring-2 focus:ring-muted-foreground/30 focus:ring-offset-2 focus:ring-offset-background"
              )}
            >
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-muted flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Zap className="size-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    I'm a Pro
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Skip the tour and start trading right away.
                    <span className="text-xs ml-1 opacity-70">(You can restart it from Settings)</span>
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Footer note */}
        <div className="px-8 pb-6 pt-2">
          <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
            <Shield className="size-3.5" />
            <span>Your preferences are saved locally</span>
          </div>
        </div>
      </div>
    </div>
  );
};

